// In your Shopify theme or app
const GADGET_API_URL = 'https://rarely.gadget.app/api/graphql';

// Get latest videos for home screen
async function fetchLatestVideos() {
  const query = `
    query GetLatestVideos {
      socialVideos(
        filter: {
          OR: [{reviewStatus: { equals: "approved" }}, {reviewStatus: { equals: "updateSubmitted" }}, {reviewStatus: { equals: "updateRejected" }}],
          isActive: { equals: true }
          isDeleted: { equals: false }
        }
        sort: [{ createdAt: Descending }]
        first: 10
      ) {
        edges {
          node {
            id
            title
            caption
            videoFile {
              url
              fileName
            }
            thumbnailImage {
              url
            }
            viewCount
            clickCount
            createdAt
            product {
              id
              rarelyShopifyProductId
              handle
              approvedSnapshot
              title
              productVariants(first: 5) {
                edges {
                    node {
                        rarelyShopifyVariantId
                    }
                }
              }
            }
            collection {
              id
              title
              handle
            }
            merchant {
              id
              companyName
              avatar {
                url
                fileName
              }
              merchantAllCollection {
                handle
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(GADGET_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    });

    const result = await response.json();
    return result.data.socialVideos.edges.map(edge => edge.node);
  } catch (error) {
    console.error('Error fetching videos:', error);
    return [];
  }
}

// Fetch return policies for all variants and return structured data
async function fetchAllVariantReturnPolicies(variantIds, merchantId) {
  // Convert numeric variant IDs to Shopify GID format for GraphQL queries
  const variantGids = variantIds.map(id => `gid://shopify/ProductVariant/${id}`);

  // DEMO MODE CONFIGURATION
  // Set to null for normal operation, or to one of: 'no-default-policy', 'single-variant-default', 'multiple-variants-mixed'
  const DEMO_MODE = null; // null, 'no-default-policy', 'single-variant-default', 'multiple-variants-mixed'

  // Demo mode implementation
  if (DEMO_MODE) {
    console.log(`🎭 DEMO MODE ACTIVE (fetchAllVariantReturnPolicies): ${DEMO_MODE}`);
    
    // Dummy default policy
    const dummyDefaultPolicy = {
      id: 'MerchantReturnPolicy-demo-default',
      name: 'Standard Return Policy',
      returnWindowDays: 30,
      returnShippingResponsibility: 'customer',
      returnShippingFlatRateFee: null,
      refundTrigger: 'on_return_delivered',
      returnRestockFee: 0,
      refundShippingPolicy: 'proportional',
      refundShippingPercentage: null
    };

    // Dummy custom policy 1
    const dummyCustomPolicy1 = {
      id: 'MerchantReturnPolicy-demo-custom1',
      name: 'Premium Return Policy',
      isDefault: false,
      returnWindowDays: 60,
      returnShippingResponsibility: 'merchant',
      returnShippingFlatRateFee: null,
      refundTrigger: 'immediate',
      returnRestockFee: 0,
      refundShippingPolicy: 'percentage',
      refundShippingPercentage: 100,
      isActive: true
    };

    // Dummy custom policy 2
    const dummyCustomPolicy2 = {
      id: 'MerchantReturnPolicy-demo-custom2',
      name: 'Extended Holiday Returns',
      isDefault: false,
      returnWindowDays: 90,
      returnShippingResponsibility: 'flat',
      returnShippingFlatRateFee: 5.99,
      refundTrigger: 'on_label_first_scan',
      returnRestockFee: 10,
      refundShippingPolicy: 'none',
      refundShippingPercentage: null,
      isActive: true
    };

    let defaultPolicy = null;
    let rarelyToMerchantVariantMap = {};
    let merchantVariantIds = [];
    let explicitAssignments = {};

    // Configure data based on demo scenario
    if (DEMO_MODE === 'no-default-policy') {
      defaultPolicy = null;
      variantGids.forEach((gid, index) => {
        const merchantVariantId = `merchant-variant-${index + 1}`;
        rarelyToMerchantVariantMap[gid] = merchantVariantId;
        merchantVariantIds.push(merchantVariantId);
      });
      explicitAssignments = {};
      console.log('📋 Demo: No default policy - all variants are final sale');
      
    } else if (DEMO_MODE === 'single-variant-default') {
      defaultPolicy = dummyDefaultPolicy;
      const variantGid = variantGids[0] || 'gid://shopify/ProductVariant/101';
      const merchantVariantId = 'merchant-variant-single';
      rarelyToMerchantVariantMap[variantGid] = merchantVariantId;
      merchantVariantIds.push(merchantVariantId);
      explicitAssignments = {};
      console.log('📋 Demo: Single variant with default policy');
      
    } else if (DEMO_MODE === 'multiple-variants-mixed') {
      defaultPolicy = dummyDefaultPolicy;
      for (let i = 1; i <= 9; i++) {
        const variantGid = variantGids[i - 1] || `gid://shopify/ProductVariant/${100 + i}`;
        const merchantVariantId = `merchant-variant-${i}`;
        rarelyToMerchantVariantMap[variantGid] = merchantVariantId;
        merchantVariantIds.push(merchantVariantId);
      }
      for (let i = 5; i <= 8; i++) {
        explicitAssignments[`merchant-variant-${i}`] = {
          policy: dummyCustomPolicy1,
          isDefault: false
        };
      }
      explicitAssignments['merchant-variant-9'] = {
        policy: dummyCustomPolicy2,
        isDefault: false
      };
      console.log('📋 Demo: 9 variants - 4 default, 4 custom policy 1, 1 custom policy 2');
    }

    // Build the result structure
    const policiesByVariant = {};
    const demoVariantIds = DEMO_MODE === 'single-variant-default' 
      ? [variantIds[0] || '101']
      : DEMO_MODE === 'multiple-variants-mixed'
      ? Array.from({ length: 9 }, (_, i) => variantIds[i] || `${101 + i}`)
      : variantIds;
    
    const demoVariantGids = demoVariantIds.map(id => 
      id.startsWith('gid://') ? id : `gid://shopify/ProductVariant/${id}`
    );
    
    demoVariantGids.forEach((rarelyVariantGid, index) => {
      const merchantVariantId = rarelyToMerchantVariantMap[rarelyVariantGid];
      const originalVariantId = demoVariantIds[index];
      
      if (!merchantVariantId) {
        return;
      }
      
      const explicitAssignment = explicitAssignments[merchantVariantId];
      
      if (!defaultPolicy) {
        policiesByVariant[originalVariantId] = {
          policy: null,
          isFinalSale: true,
          isDefault: false
        };
      } else if (explicitAssignment) {
        if (explicitAssignment.isDefault) {
          policiesByVariant[originalVariantId] = {
            policy: null,
            isFinalSale: true,
            isDefault: false
          };
        } else {
          policiesByVariant[originalVariantId] = {
            policy: explicitAssignment.policy,
            isFinalSale: false,
            isDefault: false
          };
        }
      } else {
        policiesByVariant[originalVariantId] = {
          policy: defaultPolicy,
          isFinalSale: false,
          isDefault: true
        };
      }
    });

    // Determine if all variants have the same policy
    const variantEntries = Object.values(policiesByVariant);
    let allSamePolicy = false;
    let commonPolicy = null;

    if (variantEntries.length > 0) {
      const firstEntry = variantEntries[0];
      const firstPolicyId = firstEntry.isFinalSale ? 'final-sale' : firstEntry.policy?.id;
      
      allSamePolicy = variantEntries.every(entry => {
        const entryPolicyId = entry.isFinalSale ? 'final-sale' : entry.policy?.id;
        return entryPolicyId === firstPolicyId;
      });

      if (allSamePolicy) {
        commonPolicy = firstEntry.isFinalSale ? null : firstEntry.policy;
      }
    }

    return {
      policiesByVariant,
      allSamePolicy,
      commonPolicy
    };
  }

  // Real implementation
  const firstQuery = `
    query GetMerchantDataAndVariants($variantGids: [String!]!, $merchantId: GadgetID!) {
      merchantReturnPolicies(
        filter: {
          merchant: { id: { equals: $merchantId } }
          isDefault: { equals: true }
          isActive: { equals: true }
        }
        first: 1
      ) {
        edges {
          node {
            id
            name
            returnWindowDays
            returnShippingResponsibility
            returnShippingFlatRateFee
            refundTrigger
            returnRestockFee
            refundShippingPolicy
            refundShippingPercentage
          }
        }
      }
      
      productVariants(
        filter: {
          rarelyShopifyVariantId: { in: $variantGids }
        }
      ) {
        edges {
          node {
            id
            rarelyShopifyVariantId
            shopifyVariantId
          }
        }
      }
    }
  `;

  try {
    // First fetch: Get merchant default policy and variant mapping
    const firstResponse = await fetch(GADGET_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        query: firstQuery,
        variables: { 
          variantGids,
          merchantId
        }
      })
    });

    const firstResult = await firstResponse.json();
    
    if (firstResult.errors) {
      return {
        error: firstResult.errors[0].message,
        policiesByVariant: {},
        allSamePolicy: false,
        commonPolicy: null
      };
    }

    // Extract default policy
    const defaultPolicy = firstResult.data.merchantReturnPolicies?.edges[0]?.node || null;
    
    // Build map of rarelyShopifyVariantId to shopifyVariantId (merchant variant ID)
    const rarelyToMerchantVariantMap = {};
    const merchantVariantIds = [];
    if (firstResult.data.productVariants) {
      firstResult.data.productVariants.edges.forEach(edge => {
        const node = edge.node;
        rarelyToMerchantVariantMap[node.rarelyShopifyVariantId] = node.shopifyVariantId;
        merchantVariantIds.push(node.shopifyVariantId);
      });
    }
    
    // Build map of explicit policy assignments
    let explicitAssignments = {};
    
    // Second fetch: Get return policy assignments using merchant variant IDs
    if (merchantVariantIds.length > 0) {
      const secondQuery = `
        query GetReturnPolicyAssignments($merchantVariantIds: [String!]!) {
          returnPolicyProductVariants(
            filter: {
              shopifyProductVariantId: { in: $merchantVariantIds }
            }
          ) {
            edges {
              node {
                id
                shopifyProductVariantId
                returnPolicy {
                  id
                  name
                  isDefault
                  returnWindowDays
                  returnShippingResponsibility
                  returnShippingFlatRateFee
                  refundTrigger
                  returnRestockFee
                  refundShippingPolicy
                  refundShippingPercentage
                  isActive
                }
              }
            }
          }
        }
      `;

      const secondResponse = await fetch(GADGET_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: secondQuery,
          variables: { 
            merchantVariantIds
          }
        })
      });

      const secondResult = await secondResponse.json();
      
      if (secondResult.errors) {
        return {
          error: secondResult.errors[0].message,
          policiesByVariant: {},
          allSamePolicy: false,
          commonPolicy: null
        };
      }

      if (secondResult.data.returnPolicyProductVariants) {
        secondResult.data.returnPolicyProductVariants.edges.forEach(edge => {
          const node = edge.node;
          const policy = node.returnPolicy;
          
          if (policy && policy.isActive) {
            explicitAssignments[node.shopifyProductVariantId] = {
              policy,
              isDefault: policy.isDefault
            };
          }
        });
      }
    }
    
    // Build the result structure
    const policiesByVariant = {};
    
    variantGids.forEach((rarelyVariantGid, index) => {
      const merchantVariantId = rarelyToMerchantVariantMap[rarelyVariantGid];
      const originalVariantId = variantIds[index];
      
      if (!merchantVariantId) {
        // No merchant variant found, skip
        return;
      }
      
      const explicitAssignment = explicitAssignments[merchantVariantId];
      
      if (!defaultPolicy) {
        // No default policy exists = all variants are final sale
        policiesByVariant[originalVariantId] = {
          policy: null,
          isFinalSale: true,
          isDefault: false
        };
      } else if (explicitAssignment) {
        if (explicitAssignment.isDefault) {
          // Explicit assignment to default policy = final sale
          policiesByVariant[originalVariantId] = {
            policy: null,
            isFinalSale: true,
            isDefault: false
          };
        } else {
          // Explicit assignment to custom policy
          policiesByVariant[originalVariantId] = {
            policy: explicitAssignment.policy,
            isFinalSale: false,
            isDefault: false
          };
        }
      } else {
        // No explicit assignment = use default policy
        policiesByVariant[originalVariantId] = {
          policy: defaultPolicy,
          isFinalSale: false,
          isDefault: true
        };
      }
    });

    // Determine if all variants have the same policy
    const variantEntries = Object.values(policiesByVariant);
    let allSamePolicy = false;
    let commonPolicy = null;

    if (variantEntries.length > 0) {
      const firstEntry = variantEntries[0];
      const firstPolicyId = firstEntry.isFinalSale ? 'final-sale' : firstEntry.policy?.id;
      
      allSamePolicy = variantEntries.every(entry => {
        const entryPolicyId = entry.isFinalSale ? 'final-sale' : entry.policy?.id;
        return entryPolicyId === firstPolicyId;
      });

      if (allSamePolicy) {
        commonPolicy = firstEntry.isFinalSale ? null : firstEntry.policy;
      }
    }

    return {
      policiesByVariant,
      allSamePolicy,
      commonPolicy
    };
    
  } catch (error) {
    console.error('Error fetching return policies:', error);
    return {
      error: 'Failed to load return policies. Please try again later.',
      policiesByVariant: {},
      allSamePolicy: false,
      commonPolicy: null
    };
  }
}

// Get videos for a specific collection
async function fetchCollectionVideos(collectionId, handle) {
  // if merchant all collection then we check for if on merchant page instead of collection id
  let query = `
    query GetCollectionVideos($collectionId: String!) {
      socialVideos(
        filter: {
          OR: [{reviewStatus: { equals: "approved" }}, {reviewStatus: { equals: "updateSubmitted" }}, {reviewStatus: { equals: "updateRejected" }}],
          isActive: { equals: true },
          isDeleted: { equals: false },
          collection: { rarelyShopifyCollectionId: { equals: $collectionId } } 
        }
        sort: [{ createdAt: Descending }]
      ) {
        edges {
          node {
            id
            title
            caption
            videoFile {
              url
              fileName
            }
            thumbnailImage {
              url
            }
            viewCount
            clickCount
            createdAt
            product {
              id
              rarelyShopifyProductId
              handle
              approvedSnapshot
              title
              productVariants(first: 5) {
                edges {
                    node {
                        rarelyShopifyVariantId
                    }
                }
              }
            }
            collection {
              id
              title
              handle
            }
            merchant {
              id
              companyName
              avatar {
                url
                fileName
              }
              merchantAllCollection {
                handle
              }
            }
          }
        }
      }
    }
  `;

  const handleWords = handle.split('-');
  if (handleWords[0] === 'merchant' && handleWords.length == 2) {
    const merchant_id = handleWords[1];
    // this is an 'all' collection. use showOnMerchantPage filter instead of id.
    query = `
      query GetCollectionVideos {
        socialVideos(
          filter: {
            OR: [{reviewStatus: { equals: "approved" }}, {reviewStatus: { equals: "updateSubmitted" }}, {reviewStatus: { equals: "updateRejected" }}],
            isActive: { equals: true },
            isDeleted: { equals: false },
            showOnMerchantPage: { equals: true },
            merchantId: { equals: "${merchant_id}"}
          }
          sort: [{ createdAt: Descending }]
        ) {
          edges {
            node {
              id
              title
              caption
              videoFile {
                url
                fileName
              }
              thumbnailImage {
                url
              }
              viewCount
              clickCount
              createdAt
              product {
                id
                rarelyShopifyProductId
                handle
                approvedSnapshot
                title
                productVariants(first: 5) {
                  edges {
                      node {
                          rarelyShopifyVariantId
                      }
                  }
                }
              }
              collection {
                id
                title
                handle
              }
              merchant {
                id
                companyName
                avatar {
                  url
                  fileName
                }
                merchantAllCollection {
                  handle
                }
              }
            }
          }
        }
      }
    `;
    try {
      const response = await fetch(GADGET_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query
        })
      });

      const result = await response.json();
      console.log('result', result);
      return result.data.socialVideos.edges.map(edge => edge.node);
    } catch (error) {
      console.error('Error fetching collection videos:', error);
      return [];
    }
  } else {
    try {
      const response = await fetch(GADGET_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query,
          variables: { collectionId: `gid://shopify/Collection/${collectionId}` }
        })
      });
  
      const result = await response.json();
      return result.data.socialVideos.edges.map(edge => edge.node);
    } catch (error) {
      console.error('Error fetching collection videos:', error);
      return [];
    }
  }

}

// Get videos for a specific product
async function fetchProductVideos(productId) {
  const query = `
    query GetProductVideos($productId: String!) {
      socialVideos(
        filter: {
          OR: [{reviewStatus: { equals: "approved" }}, {reviewStatus: { equals: "updateSubmitted" }}, {reviewStatus: { equals: "updateRejected" }}],
          isActive: { equals: true }
          isDeleted: { equals: false }
          product: { rarelyShopifyProductId: { equals: $productId } }
        }
        sort: [{ createdAt: Descending }]
      ) {
        edges {
          node {
            id
            title
            caption
            videoFile {
              url
              fileName
            }
            thumbnailImage {
              url
            }
            viewCount
            clickCount
            createdAt
            collection {
              id
              title
              handle
            }
            merchant {
              id
              companyName
              avatar {
                url
                fileName
              }
              merchantAllCollection {
                handle
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(GADGET_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        query,
        variables: { productId: `gid://shopify/Product/${productId}` }
      })
    });

    const result = await response.json();
    return result.data.socialVideos.edges.map(edge => edge.node);
  } catch (error) {
    console.error('Error fetching product videos:', error);
    return [];
  }
}

// Fetch most recent merchants
async function fetchLatestMerchants() {
  const query = `
    query GetLatestMerchants {
      merchants(
        sort: [{ createdAt: Descending }],
        first: 12,
        filter: { merchantAllCollection: { handle: { isSet: true } } }
      ) {
        edges {
          node {
            id
            companyName
            description
            slogan
            website
            avatar {
              url
              fileName
            }
            banner {
              url
              fileName
            }
            primaryColor
            secondaryColor
            accentColor
            backgroundColor
            textColor
            linkColor
            createdAt
            merchantAllCollection {
                rarelyCollectionId
                handle
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(GADGET_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    });

    const result = await response.json();
    return result.data.merchants.edges.map(edge => edge.node);
  } catch (error) {
    console.error('Error fetching merchants:', error);
    return [];
  }
}

async function fetchMerchant(merchantId) {
  const query = `
    query GetMerchant {
      merchant(id: ${merchantId}) {
        id
        companyName
        contactEmail
        website
      }
    }
  `;

  try {
    const response = await fetch(GADGET_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    });

    const result = await response.json();
    return result.data.merchant;
  } catch (error) {
    console.error('Error fetching merchant:', error);
    return [];
  }
}

// Redirect to merchant's collection page
function redirectToMerchantCollection(rarelyCollectionId, merchantName) {
  if (rarelyCollectionId) {
    // Redirect to the collection page using the rarelyCollectionId
    window.location.href = `/collections/${rarelyCollectionId}`;
  } else {
    console.warn(`No collection found for merchant: ${merchantName}`);
  }
}

// Initialize return policy block with variant selection support
async function initializeReturnPolicyBlock(containerId, variantIds, variantTitles, initialVariantId, merchantId) {
  // Helper function to format shipping responsibility
  function formatShippingResponsibility(value) {
    const formats = {
      'customer': 'Customer pays return shipping',
      'merchant': 'Merchant pays return shipping',
      'flat': 'Flat rate return shipping'
    };
    return formats[value] || value;
  }

  // Helper function to format refund trigger
  function formatRefundTrigger(value) {
    const formats = {
      'immediate': 'Immediately upon approval',
      'on_label_create': 'When return label is created',
      'on_label_first_scan': 'When package is first scanned',
      'on_return_delivered': 'When return is delivered'
    };
    return formats[value] || value;
  }

  // Helper function to format shipping refund policy
  function formatShippingRefundPolicy(policy, percentage) {
    if (policy === 'none') return 'No shipping refund';
    if (policy === 'percentage') return `${percentage}% of shipping cost refunded`;
    if (policy === 'proportional') return 'Proportional shipping refund';
    return policy;
  }

  // Helper function to escape HTML
  function escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  // Helper function to show loading state
  function showLoading(container) {
    container.innerHTML = `
      <div style="text-align: center; padding: 2rem;">
        <div style="display: inline-block; width: 30px; height: 30px; border: 3px solid #f3f3f3; border-top: 3px solid #333; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <p style="margin-top: 1rem; color: #666;">Loading return policy...</p>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
  }

  // Helper function to show error state
  function showError(container, message) {
    container.innerHTML = `
      <div style="padding: 1.5rem; border: 1px solid #d32f2f; border-radius: 8px; background: #ffebee; color: #c62828;">
        <p style="margin: 0; font-weight: 600;">Error</p>
        <p style="margin: 0.5rem 0 0 0;">${escapeHtml(message)}</p>
      </div>
    `;
  }

  // Helper function to show no policy state
  function showNoPolicy(container, variantTitle) {
    container.innerHTML = `
      <div>
        ${variantTitle ? `<p style="margin: 0.5rem 0 0 0; color: #666; font-size: 0.9rem;">${escapeHtml(variantTitle)}</p>` : ''}
        <p style="margin: 0.5rem 0 0 0; color: #666;">This item is <strong>final sale</strong> and cannot be returned.</p>
      </div>
    `;
  }

  // Helper function to display a policy
  function displayPolicy(container, policy, variantTitle, showVariantNote) {
    if (!policy) {
      showNoPolicy(container, variantTitle);
      return;
    }

    let html = '<div class="return-policy-block">';
    
    // html += `<h3 style="margin: 0 0 1rem 0; font-size: 1.1rem; font-weight: 600;">${escapeHtml(policy.name)}</h3>`;
    // html += `<h3 style="margin: 0 0 1rem 0; font-size: 1.1rem; font-weight: 600;">Return Policy</h3>`;
    
    if (showVariantNote && variantTitle) {
      html += `<p style="margin: 0 0 1rem 0; color: #666; font-size: 0.9rem;"><em>Applies to: ${escapeHtml(variantTitle)}</em></p>`;
    }
    
    html += '<div style="display: grid; gap: 0.75rem;">';
    
    if (policy.returnWindowDays) {
      html += `<div><span style="font-size: 0.9rem;">Accepts returns up to <strong>${policy.returnWindowDays} days</strong>.</span></div>`
      // html += `
      //   <div>
      //     <strong style="font-size: 0.9rem;">Return Window:</strong>
      //     <span style="font-size: 0.9rem;"> ${policy.returnWindowDays} days</span>
      //   </div>
      // `;
    }
    
    // html += `
    //   <div>
    //     <strong style="font-size: 0.9rem;">Return Shipping:</strong>
    //     <span style="font-size: 0.9rem;"> ${formatShippingResponsibility(policy.returnShippingResponsibility)}</span>
    //     ${policy.returnShippingFlatRateFee ? ` ($${policy.returnShippingFlatRateFee})` : ''}
    //   </div>
    // `;
    
    // html += `
    //   <div>
    //     <strong style="font-size: 0.9rem;">Refund Timing:</strong>
    //     <span style="font-size: 0.9rem;"> ${formatRefundTrigger(policy.refundTrigger)}</span>
    //   </div>
    // `;
    
    // if (policy.returnRestockFee) {
    //   html += `
    //     <div>
    //       <strong style="font-size: 0.9rem;">Restocking Fee:</strong>
    //       <span style="font-size: 0.9rem;"> ${policy.returnRestockFee}%</span>
    //     </div>
    //   `;
    // }
    
    // if (policy.refundShippingPolicy && policy.refundShippingPolicy !== 'none') {
    //   html += `
    //     <div>
    //       <strong style="font-size: 0.9rem;">Shipping Refund:</strong>
    //       <span style="font-size: 0.9rem;"> ${formatShippingRefundPolicy(policy.refundShippingPolicy, policy.refundShippingPercentage)}</span>
    //     </div>
    //   `;
    // }
    
    html += '</div></div>';
    container.innerHTML = html;
  }

  // Helper function to check if all policies are the same
  function checkIfAllPoliciesSame(policiesByVariant) {
    const entries = Object.values(policiesByVariant);
    if (entries.length === 0) return { allSame: true, policy: null };
    
    const first = entries[0];
    const firstPolicyId = first.isFinalSale ? 'final-sale' : first.policy?.id;
    
    const allSame = entries.every(entry => {
      const entryPolicyId = entry.isFinalSale ? 'final-sale' : entry.policy?.id;
      return entryPolicyId === firstPolicyId;
    });
    
    return {
      allSame,
      policy: allSame ? (first.isFinalSale ? null : first.policy) : null
    };
  }

  // Helper function to get current variant ID from product form
  function getCurrentVariantId() {
    const variantInput = document.querySelector('input[name="id"]');
    const selectElement = document.querySelector('select[name="id"]');
    
    if (variantInput) {
      return variantInput.value;
    } else if (selectElement) {
      return selectElement.value;
    }
    
    return initialVariantId;
  }

  // Helper function to set up variant change listeners
  function setupVariantListeners(policiesByVariant, allSamePolicy, container) {
    const variantInput = document.querySelector('input[name="id"]');
    const selectElement = document.querySelector('select[name="id"]');
    const variantButtons = document.querySelectorAll('[data-variant-id]');
    
    const updateDisplay = () => {
      const currentVariantId = getCurrentVariantId();
      const variantTitle = variantTitles[currentVariantId] || 'Selected variant';
      const policyData = policiesByVariant[currentVariantId];
      
      if (!policyData) {
        showNoPolicy(container, variantTitle);
        return;
      }
      
      if (policyData.isFinalSale) {
        showNoPolicy(container, variantTitle);
      } else {
        displayPolicy(container, policyData.policy, variantTitle, !allSamePolicy);
      }
    };
    
    if (variantInput) {
      variantInput.addEventListener('change', updateDisplay);
    }
    
    if (selectElement) {
      selectElement.addEventListener('change', updateDisplay);
    }
    
    variantButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(updateDisplay, 100);
      });
    });
    
    return updateDisplay;
  }

  function showOurReturnPolicy(container) {
    container.innerHTML = `
      <div class="return-policy-block">
        <div style="display: grid; gap: 0.75rem;">
          <div><span style="font-size: 0.9rem;">Returns accepted for <strong>30 days</strong>.</span></div>
          <div><span style="font-size: 0.9rem;"><strong>Free</strong> return shipping.</span></div>
        </div>
      </div>
    `;
  }

  // Main logic
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container element with ID '${containerId}' not found`);
    return;
  }

  showLoading(container);

  try {

    if ((merchantId ?? '').trim() === '') {
      showOurReturnPolicy(container);
      return;
    }

    const result = await fetchAllVariantReturnPolicies(variantIds, merchantId);
    
    if (result.error) {
      showError(container, result.error);
      return;
    }

    const { policiesByVariant, allSamePolicy, commonPolicy } = result;
    
    if (Object.keys(policiesByVariant).length === 0) {
      showNoPolicy(container, null);
      return;
    }

    // Set up listeners for variant changes
    const updateDisplay = setupVariantListeners(policiesByVariant, allSamePolicy, container);
    
    // Initial display
    if (allSamePolicy) {
      // All variants have the same policy, show it once without variant-specific note
      if (commonPolicy) {
        displayPolicy(container, commonPolicy, null, false);
      } else {
        showNoPolicy(container, null);
      }
    } else {
      // Show policy for current variant
      updateDisplay();
    }
    
  } catch (error) {
    console.error('Error initializing return policy block:', error);
    showError(container, 'Failed to load return policy. Please try again later.');
  }
}

// Fetch and render return policies for product variants
async function fetchReturnPolicies(variantIds, variantMap, contentElementId, merchantId) {
  // Helper function to show loading state
  function showLoading(contentElement) {
    contentElement.innerHTML = `
      <div style="text-align: center; padding: 2rem;">
        <div style="display: inline-block; width: 30px; height: 30px; border: 3px solid #f3f3f3; border-top: 3px solid #333; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <p style="margin-top: 1rem; color: #666;">Loading return policies...</p>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
  }

  // Helper function to show error state
  function showError(contentElement, message) {
    contentElement.innerHTML = `
      <div style="text-align: center; padding: 2rem; color: #d32f2f;">
        <p><strong>Error:</strong> ${message}</p>
      </div>
    `;
  }

  // Helper function to show empty state
  function showEmpty(contentElement) {
    contentElement.innerHTML = `
      <div style="text-align: center; padding: 2rem; color: #666;">
        <p>All sales are final.</p>
      </div>
    `;
  }

  // Helper function to format shipping responsibility
  function formatShippingResponsibility(value) {
    const formats = {
      'customer': 'Customer pays return shipping',
      'merchant': 'Merchant pays return shipping',
      'flat': 'Flat rate return shipping'
    };
    return formats[value] || value;
  }

  // Helper function to format refund trigger
  function formatRefundTrigger(value) {
    const formats = {
      'immediate': 'Immediately upon approval',
      'on_label_create': 'When return label is created',
      'on_label_first_scan': 'When package is first scanned',
      'on_return_delivered': 'When return is delivered'
    };
    return formats[value] || value;
  }

  // Helper function to render final sale section
  function renderFinalSale(variantIds) {
    const variantTitles = variantIds.map(v => variantMap[v] || v).join(', ');
    
    return `
      <div style="margin-bottom: 2rem; padding: 1.5rem; border: 1px solid #ff9800; border-radius: 8px; background: #fff3e0;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.1rem; font-weight: 600; color: #e65100;">
          Final Sale - No Returns
        </h3>
        <p style="margin: 0 0 1rem 0; color: #666; font-size: 0.9rem;"><strong>Applies to:</strong> ${variantTitles}</p>
        <p style="margin: 0; color: #666; font-size: 0.9rem;">
          These items are final sale and cannot be returned or exchanged.
        </p>
      </div>
    `;
  }

  // Helper function to render a policy
  function renderPolicy(policy, variantIds, isDefault) {
    const variantTitles = variantIds.map(v => variantMap[v] || v).join(', ');
    const policyLabel = policy.name;
    
    return `
      <div class="return-policy-block">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.1rem; font-weight: 600;">${policyLabel}</h3>
        ${variantIds.length > 1 ? 
          `<p style="margin: 0 0 1rem 0; color: #666; font-size: 0.9rem;"><strong>Applies to:</strong> ${variantTitles}</p>` :
          ''
        }
        
        <div style="display: grid; gap: 0.75rem;">
          ${policy.returnWindowDays ? `
            <div>
              <strong style="font-size: 0.9rem;">Return Window:</strong>
              <span style="font-size: 0.9rem;"> ${policy.returnWindowDays} days</span>
            </div>
          ` : ''}
          
          <div>
            <strong style="font-size: 0.9rem;">Return Shipping:</strong>
            <span style="font-size: 0.9rem;"> ${formatShippingResponsibility(policy.returnShippingResponsibility)}</span>
            ${policy.returnShippingFlatRateFee ? ` ($${policy.returnShippingFlatRateFee})` : ''}
          </div>
          
          ${policy.returnRestockFee ? `
            <div>
              <strong style="font-size: 0.9rem;">Restocking Fee:</strong>
              <span style="font-size: 0.9rem;"> ${policy.returnRestockFee}%</span>
            </div>
          ` : ''}
          
          ${policy.refundShippingPolicy && policy.refundShippingPolicy !== 'none' ? `
            <div>
              <strong style="font-size: 0.9rem;">Shipping Refund:</strong>
              <span style="font-size: 0.9rem;">
                ${policy.refundShippingPolicy === 'percentage' ? `${policy.refundShippingPercentage}% of shipping cost` : 'Proportional to returned items'}
              </span>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  // Helper function to render all policies
  function renderPolicies(contentElement, finalSaleVariants, defaultPolicyVariants, customPolicies, defaultPolicy) {
    if (finalSaleVariants.length === 0 && defaultPolicyVariants.length === 0 && Object.keys(customPolicies).length === 0) {
      showEmpty(contentElement);
      return;
    }

    let html = '<div style="padding: 1rem;">';
    
    // Render final sale items first
    if (finalSaleVariants.length > 0) {
      html += renderFinalSale(finalSaleVariants);
    }
    
    // Render default policy items
    if (defaultPolicyVariants.length > 0 && defaultPolicy) {
      html += renderPolicy(defaultPolicy, defaultPolicyVariants, true);
    }
    
    // Render custom policies
    for (const [policyId, policyData] of Object.entries(customPolicies)) {
      html += renderPolicy(policyData.policy, policyData.variants, false);
    }
    
    html += '</div>';
    contentElement.innerHTML = html;
  }

  // Main logic
  const contentElement = document.getElementById(contentElementId);
  if (!contentElement) {
    console.error(`Element with ID '${contentElementId}' not found`);
    return;
  }

  // Convert numeric variant IDs to Shopify GID format for GraphQL queries
  // Input: ['123', '456'] -> Output: ['gid://shopify/ProductVariant/123', 'gid://shopify/ProductVariant/456']
  // Keep original variantIds for use with variantMap which expects numeric IDs
  const variantGids = variantIds.map(id => `gid://shopify/ProductVariant/${id}`);

  showLoading(contentElement);

  // DEMO MODE CONFIGURATION
  // Set to null for normal operation, or to one of: 'no-default-policy', 'single-variant-default', 'multiple-variants-mixed'
  // const DEMO_MODE = null; // null, 'no-default-policy', 'single-variant-default', 'multiple-variants-mixed'
  // const DEMO_MODE = 'do-default-policy';
  // const DEMO_MODE = 'single-variant-default';
  const DEMO_MODE = 'multiple-variants-mixed';

  // Demo mode implementation
  if (DEMO_MODE) {
    console.log(`🎭 DEMO MODE ACTIVE: ${DEMO_MODE}`);
    
    // Dummy default policy
    const dummyDefaultPolicy = {
      id: 'MerchantReturnPolicy-demo-default',
      name: 'Standard Return Policy',
      returnWindowDays: 30,
      returnShippingResponsibility: 'customer',
      returnShippingFlatRateFee: null,
      refundTrigger: 'on_return_delivered',
      returnRestockFee: 0,
      refundShippingPolicy: 'proportional',
      refundShippingPercentage: null
    };

    // Dummy custom policy 1
    const dummyCustomPolicy1 = {
      id: 'MerchantReturnPolicy-demo-custom1',
      name: 'Premium Return Policy',
      isDefault: false,
      returnWindowDays: 60,
      returnShippingResponsibility: 'merchant',
      returnShippingFlatRateFee: null,
      refundTrigger: 'immediate',
      returnRestockFee: 0,
      refundShippingPolicy: 'percentage',
      refundShippingPercentage: 100,
      isActive: true
    };

    // Dummy custom policy 2
    const dummyCustomPolicy2 = {
      id: 'MerchantReturnPolicy-demo-custom2',
      name: 'Extended Holiday Returns',
      isDefault: false,
      returnWindowDays: 90,
      returnShippingResponsibility: 'flat',
      returnShippingFlatRateFee: 5.99,
      refundTrigger: 'on_label_first_scan',
      returnRestockFee: 10,
      refundShippingPolicy: 'none',
      refundShippingPercentage: null,
      isActive: true
    };

    let defaultPolicy = null;
    let rarelyToMerchantVariantMap = {};
    let merchantVariantIds = [];
    let explicitAssignments = {};

    // Configure data based on demo scenario
    if (DEMO_MODE === 'no-default-policy') {
      // Scenario: No default policy exists (all variants are final sale)
      defaultPolicy = null;
      
      // Create variant mappings for provided variantIds
      variantGids.forEach((gid, index) => {
        const merchantVariantId = `merchant-variant-${index + 1}`;
        rarelyToMerchantVariantMap[gid] = merchantVariantId;
        merchantVariantIds.push(merchantVariantId);
      });
      
      // No explicit assignments
      explicitAssignments = {};
      
      console.log('📋 Demo: No default policy - all variants are final sale');
      
    } else if (DEMO_MODE === 'single-variant-default') {
      // Scenario: Single variant with default policy, no explicit assignment
      defaultPolicy = dummyDefaultPolicy;
      
      // Use first variant from variantIds or create dummy
      const variantGid = variantGids[0] || 'gid://shopify/ProductVariant/101';
      const merchantVariantId = 'merchant-variant-single';
      
      rarelyToMerchantVariantMap[variantGid] = merchantVariantId;
      merchantVariantIds.push(merchantVariantId);
      
      // No explicit assignment (will use default)
      explicitAssignments = {};
      
      console.log('📋 Demo: Single variant with default policy');
      
    } else if (DEMO_MODE === 'multiple-variants-mixed') {
      // Scenario: 9 variants across 3 policies
      defaultPolicy = dummyDefaultPolicy;
      
      // Create 9 variant mappings
      for (let i = 1; i <= 9; i++) {
        const variantGid = variantGids[i - 1] || `gid://shopify/ProductVariant/${100 + i}`;
        const merchantVariantId = `merchant-variant-${i}`;
        
        rarelyToMerchantVariantMap[variantGid] = merchantVariantId;
        merchantVariantIds.push(merchantVariantId);
      }
      
      // Variants 1-4: No explicit assignment (use default)
      // (no entries in explicitAssignments)
      
      // Variants 5-8: Explicitly assigned to custom policy 1
      for (let i = 5; i <= 8; i++) {
        explicitAssignments[`merchant-variant-${i}`] = {
          policy: dummyCustomPolicy1,
          isDefault: false
        };
      }
      
      // Variant 9: Explicitly assigned to custom policy 2
      explicitAssignments['merchant-variant-9'] = {
        policy: dummyCustomPolicy2,
        isDefault: false
      };
      
      console.log('📋 Demo: 9 variants - 4 default, 4 custom policy 1, 1 custom policy 2');
    }

    console.log('🔍 Demo data prepared:', {
      defaultPolicy: defaultPolicy?.name || 'none',
      variantCount: merchantVariantIds.length,
      explicitAssignments: Object.keys(explicitAssignments).length
    });

    // Skip API calls and jump directly to categorization logic
    const finalSaleVariants = [];
    const defaultPolicyVariants = [];
    const customPolicies = {};
    
    // Use the same categorization logic but with the appropriate variantIds
    const demoVariantIds = DEMO_MODE === 'single-variant-default' 
      ? [variantIds[0] || '101']
      : DEMO_MODE === 'multiple-variants-mixed'
      ? Array.from({ length: 9 }, (_, i) => variantIds[i] || `${101 + i}`)
      : variantIds;
    
    const demoVariantGids = demoVariantIds.map(id => {
      // If it's already a GID, use it; otherwise create one
      return id.startsWith('gid://') ? id : `gid://shopify/ProductVariant/${id}`;
    });
    
    demoVariantGids.forEach((rarelyVariantGid, index) => {
      const merchantVariantId = rarelyToMerchantVariantMap[rarelyVariantGid];
      const originalVariantId = demoVariantIds[index];
      
      if (!merchantVariantId) {
        return;
      }
      
      const explicitAssignment = explicitAssignments[merchantVariantId];
      
      if (!defaultPolicy) {
        finalSaleVariants.push(originalVariantId);
      } else if (explicitAssignment) {
        if (explicitAssignment.isDefault) {
          finalSaleVariants.push(originalVariantId);
        } else {
          const policyId = explicitAssignment.policy.id;
          if (!customPolicies[policyId]) {
            customPolicies[policyId] = {
              policy: explicitAssignment.policy,
              variants: []
            };
          }
          customPolicies[policyId].variants.push(originalVariantId);
        }
      } else {
        defaultPolicyVariants.push(originalVariantId);
      }
    });

    console.log('📊 Demo categorization:', {
      finalSale: finalSaleVariants.length,
      defaultPolicy: defaultPolicyVariants.length,
      customPolicies: Object.keys(customPolicies).length
    });

    renderPolicies(contentElement, finalSaleVariants, defaultPolicyVariants, customPolicies, defaultPolicy);
    return;
  }

  const firstQuery = `
    query GetMerchantDataAndVariants($variantGids: [String!]!, $merchantId: GadgetID!) {
      merchantReturnPolicies(
        filter: {
          merchant: { id: { equals: $merchantId } }
          isDefault: { equals: true }
          isActive: { equals: true }
        }
        first: 1
      ) {
        edges {
          node {
            id
            name
            returnWindowDays
            returnShippingResponsibility
            returnShippingFlatRateFee
            refundTrigger
            returnRestockFee
            refundShippingPolicy
            refundShippingPercentage
          }
        }
      }
      
      productVariants(
        filter: {
          rarelyShopifyVariantId: { in: $variantGids }
        }
      ) {
        edges {
          node {
            id
            rarelyShopifyVariantId
            shopifyVariantId
          }
        }
      }
    }
  `;

  try {
    // First fetch: Get merchant default policy and variant mapping
    const firstResponse = await fetch(GADGET_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        query: firstQuery,
        variables: { 
          variantGids,
          merchantId
        }
      })
    });

    const firstResult = await firstResponse.json();
    console.log('first result', firstResult);
    
    if (firstResult.errors) {
      showError(contentElement, firstResult.errors[0].message);
      return;
    }

    // Extract default policy
    const defaultPolicy = firstResult.data.merchantReturnPolicies?.edges[0]?.node || null;
    
    // Build map of rarelyShopifyVariantId to shopifyVariantId (merchant variant ID)
    const rarelyToMerchantVariantMap = {};
    const merchantVariantIds = [];
    if (firstResult.data.productVariants) {
      firstResult.data.productVariants.edges.forEach(edge => {
        const node = edge.node;
        rarelyToMerchantVariantMap[node.rarelyShopifyVariantId] = node.shopifyVariantId;
        merchantVariantIds.push(node.shopifyVariantId);
      });
    }
    
    // Build map of explicit policy assignments
    let explicitAssignments = {};
    
    // Second fetch: Get return policy assignments using merchant variant IDs
    if (merchantVariantIds.length > 0) {
      const secondQuery = `
        query GetReturnPolicyAssignments($merchantVariantIds: [String!]!) {
          returnPolicyProductVariants(
            filter: {
              shopifyProductVariantId: { in: $merchantVariantIds }
            }
          ) {
            edges {
              node {
                id
                shopifyProductVariantId
                returnPolicy {
                  id
                  name
                  isDefault
                  returnWindowDays
                  returnShippingResponsibility
                  returnShippingFlatRateFee
                  refundTrigger
                  returnRestockFee
                  refundShippingPolicy
                  refundShippingPercentage
                  isActive
                }
              }
            }
          }
        }
      `;

      const secondResponse = await fetch(GADGET_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: secondQuery,
          variables: { 
            merchantVariantIds
          }
        })
      });

      const secondResult = await secondResponse.json();
      console.log('second result', secondResult);
      
      if (secondResult.errors) {
        showError(contentElement, secondResult.errors[0].message);
        return;
      }

      if (secondResult.data.returnPolicyProductVariants) {
        secondResult.data.returnPolicyProductVariants.edges.forEach(edge => {
          const node = edge.node;
          const policy = node.returnPolicy;
          
          if (policy && policy.isActive) {
            explicitAssignments[node.shopifyProductVariantId] = {
              policy,
              isDefault: policy.isDefault
            };
          }
        });
      }
    }
    
    // Categorize variants
    const finalSaleVariants = [];
    const defaultPolicyVariants = [];
    const customPolicies = {};
    
    variantGids.forEach((rarelyVariantGid, index) => {
      const merchantVariantId = rarelyToMerchantVariantMap[rarelyVariantGid];
      const originalVariantId = variantIds[index]; // For display with variantMap
      
      if (!merchantVariantId) {
        // No merchant variant found, skip
        return;
      }
      
      const explicitAssignment = explicitAssignments[merchantVariantId];
      
      if (!defaultPolicy) {
        // No default policy exists = all variants are final sale
        finalSaleVariants.push(originalVariantId);
      } else if (explicitAssignment) {
        if (explicitAssignment.isDefault) {
          // Explicit assignment to default policy = final sale
          finalSaleVariants.push(originalVariantId);
        } else {
          // Explicit assignment to custom policy
          const policyId = explicitAssignment.policy.id;
          if (!customPolicies[policyId]) {
            customPolicies[policyId] = {
              policy: explicitAssignment.policy,
              variants: []
            };
          }
          customPolicies[policyId].variants.push(originalVariantId);
        }
      } else {
        // No explicit assignment = use default policy (implied default)
        defaultPolicyVariants.push(originalVariantId);
      }
    });

    renderPolicies(contentElement, finalSaleVariants, defaultPolicyVariants, customPolicies, defaultPolicy);
  } catch (error) {
    console.error('Error fetching return policies:', error);
    showError(contentElement, 'Failed to load return policies. Please try again later.');
  }
}

